import { z } from "zod";
import { CATEGORY_MAX, NOTE_CATEGORIES_MAX, NOTE_FORMATS, NOTE_SOURCES, NOTE_STATUSES } from "./constants";

export const noteSourceSchema = z.enum(NOTE_SOURCES);
export const noteStatusSchema = z.enum(NOTE_STATUSES);
export const noteFormatSchema = z.enum(NOTE_FORMATS);

const categoriesSchema = z.array(z.string().max(CATEGORY_MAX + 1)).max(NOTE_CATEGORIES_MAX * 2);

export const createNoteSchema = z.object({
  title: z.string().max(500).default(""),
  bodyText: z.string().max(1_000_000).default(""),
  bodyHtml: z.string().max(5_000_000).nullish(),
  format: noteFormatSchema.default("text"),
  /** One category (older clients). Added before `categories` when both are given. */
  category: z.string().max(CATEGORY_MAX + 1).nullish(),
  categories: categoriesSchema.optional(),
  source: noteSourceSchema.default("web"),
  sourceUrl: z.url().max(2048).nullish(),
});
export type CreateNoteInput = z.input<typeof createNoteSchema>;

export const updateNoteSchema = z.object({
  title: z.string().max(500).optional(),
  bodyText: z.string().max(1_000_000).optional(),
  format: noteFormatSchema.optional(),
  /** Replaces all categories with this one (null: none). Older clients; prefer `categories`. */
  category: z.string().max(CATEGORY_MAX + 1).nullable().optional(),
  /** Replaces all categories. */
  categories: categoriesSchema.optional(),
  status: noteStatusSchema.optional(),
  pinned: z.boolean().optional(),
});
export type UpdateNoteInput = z.infer<typeof updateNoteSchema>;

export const listNotesQuerySchema = z.object({
  category: z.string().optional(),
  status: noteStatusSchema.default("active"),
  q: z.string().max(200).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListNotesQuery = z.infer<typeof listNotesQuerySchema>;

export interface Note {
  id: string;
  title: string;
  bodyText: string;
  bodyHtml: string | null;
  format: z.infer<typeof noteFormatSchema>;
  /** The note's categories (#tags), in the order they were added. */
  categories: string[];
  /** The first of `categories`, for older clients. */
  category: string | null;
  source: z.infer<typeof noteSourceSchema>;
  sourceUrl: string | null;
  fromAddr: string | null;
  status: z.infer<typeof noteStatusSchema>;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  slug: string;
  name: string;
  color: string | null;
  /** Active notes with this category. */
  noteCount: number;
  /** Notes with it in any state (archived and trashed too); 0 means it can be deleted without touching a note. */
  totalCount: number;
}
